import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo } from "react";
import toast from "react-hot-toast";

import { setAccessTokenGetter, setOnForbidden, setOnUnauthorized } from "../api/client";
import type { AuthContextValue, AuthUser } from "./authContext";
import { AuthContext } from "./authContext";

/** Fake dev user used when VITE_BYPASS_SSO=true. */
const DEV_USER: AuthUser = {
  id: "dev-bypass-user",
  email: "dev@localhost",
  displayName: "Dev User",
  tenantId: "dev-tenant",
};

// The local backend resolves personas from the Entra object id claim. Keep
// this development-only token aligned with the seeded local user so the
// bypassed UI exercises the same authenticated code path as production.
const devEntraObjectId = import.meta.env.VITE_DEV_ENTRA_OBJECT_ID?.trim() || "dev-user-id";
const DEV_ACCESS_TOKEN = `eyJhbGciOiJub25lIn0.${btoa(JSON.stringify({ oid: devEntraObjectId }))
  .replace(/\+/g, "-")
  .replace(/\//g, "_")
  .replace(/=+$/, "")}.x`;

/**
 * Drop-in replacement for AuthProvider when VITE_BYPASS_SSO=true.
 * Always reports the user as authenticated with a fake dev identity.
 * No MSAL is involved — safe to use without Azure AD config.
 */
export function BypassAuthProvider({ children }: { children: ReactNode }) {
  const login = useCallback(() => {}, []);
  const logout = useCallback(async () => {}, []);
  const silentLogout = useCallback(async () => {}, []);
  const getInitials = useCallback(() => "DU", []);

  // Clear any 401 handler — backend is in dev mode so no real auth needed.
  useEffect(() => {
    setAccessTokenGetter(() => DEV_ACCESS_TOKEN);
    setOnUnauthorized(null);
    setOnForbidden(() => {
      toast.error("You do not have permission to perform this action.");
    });
    return () => {
      setAccessTokenGetter(() => null);
      setOnForbidden(null);
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: DEV_USER,
      isAuthenticated: true,
      isLoading: false,
      login,
      logout,
      silentLogout,
      getInitials,
    }),
    [login, logout, silentLogout, getInitials]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
